import React, { useState } from "react";
import { Dialog } from "@excalidraw/excalidraw/components/Dialog";
import { useAtom } from "../app-jotai";
import {
  activeCanvasAtom,
  authSessionAtom,
  canvasListDialogStateAtom,
  saveCanvasDialogStateAtom,
  cloudSaveStatusAtom,
} from "./auth-state";
import { canvasApi } from "./auth-api";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";
import "./SaveCanvasDialog.scss";

export const SaveCanvasDialog: React.FC<{
  excalidrawAPI: ExcalidrawImperativeAPI | null;
}> = ({ excalidrawAPI }) => {
  const [dialogState, setDialogState] = useAtom(saveCanvasDialogStateAtom);
  const [, setCanvasListDialog] = useAtom(canvasListDialogStateAtom);
  const [session] = useAtom(authSessionAtom);
  const [activeCanvas, setActiveCanvas] = useAtom(activeCanvasAtom);
  const [, setCloudSaveStatus] = useAtom(cloudSaveStatusAtom);

  const [name, setName] = useState(activeCanvas?.name || "");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [saveAsCopy, setSaveAsCopy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sync title when dialog opens
  React.useEffect(() => {
    if (dialogState.isOpen) {
      if (activeCanvas) {
        setName(activeCanvas.name);
        setSaveAsCopy(false);
      } else {
        setName("");
        setSaveAsCopy(false);
      }
      setError(null);
    }
  }, [dialogState.isOpen, activeCanvas]);

  if (!dialogState.isOpen) {
    return null;
  }

  const handleClose = () => {
    setDialogState({ isOpen: false });
    setError(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!excalidrawAPI || !session.accessToken) {
      setError("Please sign in to save your canvas to the cloud");
      return;
    }

    if (!name.trim()) {
      setError("Please enter a canvas name");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const allFiles = excalidrawAPI.getFiles();

      // Only include files that are actually referenced by active image elements
      const activeFileIds = new Set(
        elements
          .filter((el) => !el.isDeleted && "fileId" in el && Boolean((el as any).fileId))
          .map((el) => (el as any).fileId as string),
      );
      const files: Record<string, any> = {};
      for (const fileId of activeFileIds) {
        if (allFiles[fileId]) {
          files[fileId] = allFiles[fileId];
        }
      }

      const canvasPayload = {
        name: name.trim(),
        description: description.trim() || undefined,
        data: {
          elements,
          appState: {
            viewBackgroundColor: appState.viewBackgroundColor,
            gridSize: appState.gridSize,
            scrollX: appState.scrollX,
            scrollY: appState.scrollY,
            zoom: appState.zoom,
          },
          files,
        },
        isPublic,
      };

      if (activeCanvas && !saveAsCopy) {
        // Update existing canvas
        const res = await canvasApi.update(
          activeCanvas.id,
          canvasPayload,
          session.accessToken,
        );
        setActiveCanvas({ id: res.canvas.id, name: res.canvas.name });
        setCloudSaveStatus({
          status: "saved",
          message: `Saved "${res.canvas.name}" to Cloud successfully!`,
          timestamp: Date.now(),
        });
        excalidrawAPI.setToast({
          message: `Saved "${res.canvas.name}" to Cloud successfully!`,
          duration: 3500,
          closable: true,
        });
      } else {
        // Create new canvas
        const res = await canvasApi.create(canvasPayload, session.accessToken);
        setActiveCanvas({ id: res.canvas.id, name: res.canvas.name });
        setCloudSaveStatus({
          status: "saved",
          message: `Saved "${res.canvas.name}" to Cloud successfully!`,
          timestamp: Date.now(),
        });
        excalidrawAPI.setToast({
          message: `Saved "${res.canvas.name}" to Cloud successfully!`,
          duration: 3500,
          closable: true,
        });
      }

      handleClose();

    } catch (err: any) {
      setError(err.message || "Failed to save canvas to cloud");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      size="small"
      autofocus={false}
      onCloseRequest={handleClose}
      title={
        <div className="SaveCanvasDialog__header-title">
          <span>Save Canvas to Cloud</span>
          <button

            type="button"
            className="SaveCanvasDialog__close-btn"
            onClick={handleClose}
            title="Close (Esc)"
            aria-label="Close"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>
      }
    >
      <div
        className="SaveCanvasDialog"
        onKeyDown={(e) => e.stopPropagation()}
        onKeyUp={(e) => e.stopPropagation()}
      >
        {error && (
          <div
            style={{
              padding: "0.6rem 0.8rem",
              background: "rgba(250, 82, 82, 0.1)",
              borderRadius: "8px",
              color: "#e03131",
              fontSize: "0.85rem",
            }}
          >
            {error}
          </div>
        )}

        <form className="SaveCanvasDialog__form" onSubmit={handleSave}>
          <div className="SaveCanvasDialog__field">
            <label htmlFor="canvas-name">Canvas Title</label>
            <input
              id="canvas-name"
              type="text"
              placeholder="e.g. Microservices Architecture"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="SaveCanvasDialog__field">
            <label htmlFor="canvas-desc">Description (optional)</label>
            <textarea
              id="canvas-desc"
              placeholder="Add notes or context for this diagram..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {activeCanvas && (
            <label className="SaveCanvasDialog__checkbox-field">
              <input
                type="checkbox"
                checked={saveAsCopy}
                onChange={(e) => setSaveAsCopy(e.target.checked)}
              />
              <span>Save as a new canvas copy (keep original unchanged)</span>
            </label>
          )}

          <label className="SaveCanvasDialog__checkbox-field">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
            />
            <span>Allow public viewing for this canvas</span>
          </label>

          <div className="SaveCanvasDialog__actions">
            <button
              type="button"
              className="SaveCanvasDialog__cancel"
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="SaveCanvasDialog__submit"
              disabled={isLoading}
            >
              {isLoading
                ? "Saving..."
                : activeCanvas && !saveAsCopy
                ? `Update "${name || activeCanvas.name}"`
                : "Save to Cloud"}
            </button>
          </div>
        </form>
      </div>
    </Dialog>

  );
};
